/**
 * Almacén de estado del proyecto abierto (Zustand). La interfaz lee de él; nunca guarda reglas de
 * negocio: toda edición es un comando del editor con su inverso.
 */

import { create } from 'zustand';
import { CATALOGO } from '../../catalogo/catalogo';
import type { Comando } from '../../editor/comandos';
import {
  aplicarComando,
  deshacer,
  historialVacio,
  rehacer,
  type Historial,
} from '../../editor/historial';
import type { Espacio, Proyecto, Punto, TipoVia } from '../../nucleo/dominio/espacio';
import { formaPorId } from '../../nucleo/espacial/formas';
import { validarEspacio, type ResultadoValidacion } from '../../nucleo/validacion/espacio';

export type Herramienta =
  | { tipo: 'seleccionar' }
  /** `giro`: grados que la tecla R suma al giro automático del elemento por colocar. */
  | { tipo: 'colocar'; entradaId: string; giro?: number }
  | { tipo: 'via'; tipoVia: TipoVia; puntos: Punto[] }
  /** Medir (paso 1.4): `a` es el primer punto; con `b`, la medida queda fija. */
  | { tipo: 'medir'; a?: Punto; b?: Punto }
  /** Dibujar un muro o una zona de piso libre, punto a punto (paso 1.4). */
  | { tipo: 'muro'; puntos: Punto[] }
  | { tipo: 'zona'; puntos: Punto[] }
  /** Mano (H, o mientras se mantiene la barra espaciadora): arrastrar desplaza sin mover nada. */
  | { tipo: 'mano'; temporal?: boolean };

export type EstadoGuardado = 'guardado' | 'guardando' | 'pendiente' | 'error';

export type Modo = 'editar' | 'simular';

/** Operación de riesgo recién aplicada, con el proyecto tal como estaba antes (paso 1.5). */
export interface AntesDeRiesgo {
  proyecto: Proyecto;
  operacion: string;
  detalle?: string;
  /** Correlativo: cada operación de riesgo es un aviso nuevo aunque se repita la misma. */
  n: number;
}

interface EstadoEditor {
  proyecto: Proyecto | null;
  espacioId: string | null;
  historial: Historial;
  /** Contador de ediciones: el autoguardado lo observa. */
  cambios: number;
  seleccion: string[];
  /** Punto seleccionado de la vía seleccionada (paso 1.4): Supr lo borra. */
  puntoVia: { viaId: string; indice: number } | null;
  herramienta: Herramienta;
  modo: Modo;
  /** Desplazamiento en curso (vista previa); se confirma con un comando al soltar. */
  arrastre: { ids: string[]; dx: number; dz: number } | null;
  /** Marco mientras se arrastra una de sus manijas (vista previa de cotas y áreas). */
  marcoEnArrastre: Espacio['marco'] | null;
  guardado: EstadoGuardado;
  errorGuardado: string | null;
  /**
   * El proyecto se guardó desde otra pestaña (paso 1.5): el autoguardado se detiene hasta elegir
   * qué cambios conservar, para que ninguna pestaña pise en silencio a la otra.
   */
  cambioEnOtraPestana: boolean;
  /** La página guarda una versión «Antes de …» cuando esto cambia (historial de versiones). */
  antesDeRiesgo: AntesDeRiesgo | null;
  cargar: (p: Proyecto) => void;
  ejecutar: (c: Comando) => boolean;
  deshacer: () => void;
  rehacer: () => void;
  seleccionar: (ids: string[], agregar?: boolean) => void;
  /** Selecciona la vía y uno de sus puntos (`null` = ninguno). */
  seleccionarPuntoVia: (viaId: string, indice: number | null) => void;
  setHerramienta: (h: Herramienta) => void;
  setModo: (m: Modo) => void;
  setArrastre: (a: EstadoEditor['arrastre']) => void;
  setMarcoEnArrastre: (m: Espacio['marco'] | null) => void;
  setGuardado: (g: EstadoGuardado, error?: string | null) => void;
  /** Después de guardar: fecha y lugar (espacio de trabajo y carpeta) tal como quedaron guardados. */
  marcarGuardado: (
    guardado: Pick<Proyecto, 'actualizadoEn' | 'espacioDeTrabajoId' | 'carpetaId'>,
  ) => void;
  setCambioEnOtraPestana: (si: boolean) => void;
  /** Pasa a editar otro espacio de la pizarra (paso 1.4): sin selección ni herramienta activa. */
  activarEspacio: (espacioId: string) => void;
}

export const useEditor = create<EstadoEditor>((set, get) => ({
  proyecto: null,
  espacioId: null,
  historial: historialVacio(),
  cambios: 0,
  seleccion: [],
  puntoVia: null,
  herramienta: { tipo: 'seleccionar' },
  modo: 'editar',
  arrastre: null,
  marcoEnArrastre: null,
  guardado: 'guardado',
  errorGuardado: null,
  cambioEnOtraPestana: false,
  antesDeRiesgo: null,
  cargar: (p) =>
    set({
      proyecto: p,
      espacioId: p.espacios[0]?.id ?? null,
      historial: historialVacio(),
      cambios: 0,
      seleccion: [],
      puntoVia: null,
      herramienta: { tipo: 'seleccionar' },
      modo: 'editar',
      arrastre: null,
      marcoEnArrastre: null,
      guardado: 'guardado',
      errorGuardado: null,
      cambioEnOtraPestana: false,
      antesDeRiesgo: null,
    }),
  ejecutar: (c) => {
    const { proyecto, historial } = get();
    if (!proyecto) return false;
    const r = aplicarComando(proyecto, c, historial);
    if (!r.cambio) return false;
    const espacioId = espacioVigente(r.proyecto, get().espacioId);
    set({
      proyecto: r.proyecto,
      historial: r.historial,
      cambios: get().cambios + 1,
      guardado: 'pendiente',
      ...(c.riesgo
        ? {
            antesDeRiesgo: {
              proyecto,
              operacion: c.riesgo.operacion,
              ...(c.riesgo.detalle ? { detalle: c.riesgo.detalle } : {}),
              n: (get().antesDeRiesgo?.n ?? 0) + 1,
            },
          }
        : {}),
      ...(espacioId !== get().espacioId
        ? { espacioId, seleccion: [], puntoVia: null, marcoEnArrastre: null }
        : {}),
    });
    return true;
  },
  deshacer: () => {
    const { proyecto, historial } = get();
    if (!proyecto) return;
    const r = deshacer(proyecto, historial);
    if (!r) return;
    set({
      proyecto: r.proyecto,
      historial: r.historial,
      cambios: get().cambios + 1,
      guardado: 'pendiente',
      espacioId: espacioVigente(r.proyecto, get().espacioId),
      seleccion: limpiar(r.proyecto, get()),
      puntoVia: limpiarPunto(r.proyecto, get()),
    });
  },
  rehacer: () => {
    const { proyecto, historial } = get();
    if (!proyecto) return;
    const r = rehacer(proyecto, historial);
    if (!r) return;
    set({
      proyecto: r.proyecto,
      historial: r.historial,
      cambios: get().cambios + 1,
      guardado: 'pendiente',
      espacioId: espacioVigente(r.proyecto, get().espacioId),
      seleccion: limpiar(r.proyecto, get()),
      puntoVia: limpiarPunto(r.proyecto, get()),
    });
  },
  seleccionar: (ids, agregar = false) => {
    if (!agregar) return set({ seleccion: ids, puntoVia: null });
    const actual = new Set(get().seleccion);
    for (const id of ids) {
      if (actual.has(id)) actual.delete(id);
      else actual.add(id);
    }
    set({ seleccion: [...actual], puntoVia: null });
  },
  seleccionarPuntoVia: (viaId, indice) =>
    set({ seleccion: [viaId], puntoVia: indice === null ? null : { viaId, indice } }),
  setHerramienta: (h) =>
    set({
      herramienta: h,
      seleccion: h.tipo === 'seleccionar' ? get().seleccion : [],
      puntoVia: h.tipo === 'seleccionar' ? get().puntoVia : null,
    }),
  setModo: (m) =>
    set({
      modo: m,
      herramienta: { tipo: 'seleccionar' },
      arrastre: null,
      marcoEnArrastre: null,
      puntoVia: null,
    }),
  setArrastre: (a) => set({ arrastre: a }),
  setMarcoEnArrastre: (m) => set({ marcoEnArrastre: m }),
  setGuardado: (g, error = null) => set({ guardado: g, errorGuardado: error }),
  activarEspacio: (espacioId) => {
    if (espacioId === get().espacioId) return;
    if (!get().proyecto?.espacios.some((e) => e.id === espacioId)) return;
    set({
      espacioId,
      seleccion: [],
      puntoVia: null,
      herramienta: { tipo: 'seleccionar' },
      arrastre: null,
      marcoEnArrastre: null,
    });
  },
  marcarGuardado: ({ actualizadoEn, espacioDeTrabajoId, carpetaId }) => {
    const p = get().proyecto;
    if (!p) return;
    const { carpetaId: _c, ...resto } = p;
    set({
      proyecto: {
        ...resto,
        actualizadoEn,
        espacioDeTrabajoId,
        ...(carpetaId ? { carpetaId } : {}),
      },
    });
  },
  setCambioEnOtraPestana: (si) => set({ cambioEnOtraPestana: si }),
}));

/** El espacio activo, si sigue existiendo; si no (se eliminó o se deshizo), el primero. */
function espacioVigente(p: Proyecto, espacioId: string | null): string | null {
  return p.espacios.some((e) => e.id === espacioId) ? espacioId : (p.espacios[0]?.id ?? null);
}

/** Quita de la selección lo que ya no existe (después de deshacer o rehacer). */
function limpiar(p: Proyecto, s: EstadoEditor): string[] {
  const e = p.espacios.find((x) => x.id === s.espacioId);
  if (!e) return [];
  const l = e.layout;
  const ids = new Set([...l.elementos, ...l.vias, ...l.pisos, ...l.muros].map((x) => x.id));
  return s.seleccion.filter((id) => ids.has(id));
}

/** El punto seleccionado (de una vía, un piso o un muro) sigue existiendo (al deshacer o rehacer)? */
function limpiarPunto(p: Proyecto, s: EstadoEditor): EstadoEditor['puntoVia'] {
  const e = p.espacios.find((x) => x.id === s.espacioId);
  const f = s.puntoVia && e ? formaPorId(e.layout, s.puntoVia.viaId) : null;
  return f && s.puntoVia!.indice < f.puntos.length ? s.puntoVia : null;
}

export function useEspacio(): Espacio | null {
  return useEditor((s) => s.proyecto?.espacios.find((e) => e.id === s.espacioId) ?? null);
}

const cacheValidacion = new WeakMap<Espacio, ResultadoValidacion>();

/** Validación en vivo, memorizada por versión inmutable del espacio. */
export function validacionDe(espacio: Espacio): ResultadoValidacion {
  let v = cacheValidacion.get(espacio);
  if (!v) {
    v = validarEspacio(espacio, CATALOGO);
    cacheValidacion.set(espacio, v);
  }
  return v;
}

export function useValidacion(): ResultadoValidacion | null {
  const espacio = useEspacio();
  return espacio ? validacionDe(espacio) : null;
}
